import { Formik, Form, useFormikContext } from 'formik';

import { Widget, WidgetBody } from '@@/Widget';

import { TypedPolicy } from '../../../types';
import { PolicyConfigurationFormSection } from '../../PolicyConfigurationFormSection';
import { PolicyFormFooter } from '../../PolicyFormFooter';

import { DockerRegistryFormFields } from './DockerRegistryFormFields';
import { DockerRegistryFormValues } from './types';
import { useDockerRegistryInitialValues } from './useDockerRegistryInitialValues';
import { dockerRegistryValidationSchema } from './dockerRegistryValidationSchema';

interface Props {
  /** Form submission handler */
  onSubmit: (values: DockerRegistryFormValues) => Promise<void>;
  /** Form mode - affects button labels and validation behavior */
  mode: 'create' | 'edit';
  /** Existing policy for edit mode - provides initial values */
  policy?: TypedPolicy<unknown>;
  /** Wizard back navigation - only used in create mode */
  onBack?: () => void;
  /** Whether back is enabled - only used in create mode */
  canGoBack?: boolean;
}

/**
 * Docker Registry Policy Form Component
 *
 * Used by:
 * - PolicyCreateView (step 2): mode="create" with wizard navigation props
 * - ConfigTab (edit view): mode="edit" without wizard navigation
 */
export function DockerRegistryPolicyForm({
  onSubmit,
  mode,
  policy,
  onBack,
  canGoBack,
}: Props) {
  const initialValues = useDockerRegistryInitialValues(policy);
  const originalEnvironmentGroupIds = policy?.EnvironmentGroups || [];

  return (
    <Formik<DockerRegistryFormValues>
      initialValues={initialValues}
      validationSchema={dockerRegistryValidationSchema}
      onSubmit={onSubmit}
      enableReinitialize={mode === 'edit'}
      validateOnMount
    >
      <DockerRegistryPolicyInnerForm
        mode={mode}
        policyId={policy?.Id ?? null}
        originalEnvironmentGroupIds={originalEnvironmentGroupIds}
        onBack={onBack}
        canGoBack={canGoBack}
      />
    </Formik>
  );
}

/**
 * Inner component that has access to Formik context.
 * Renders Widget with form fields and PolicyFormFooter.
 */
function DockerRegistryPolicyInnerForm({
  mode,
  policyId,
  originalEnvironmentGroupIds,
  onBack,
  canGoBack,
}: {
  mode: 'create' | 'edit';
  policyId: number | null;
  originalEnvironmentGroupIds: Array<number>;
  onBack?: () => void;
  canGoBack?: boolean;
}) {
  const { values, setFieldValue } =
    useFormikContext<DockerRegistryFormValues>();

  return (
    <Form className="form-horizontal">
      <Widget>
        <WidgetBody>
          {mode === 'create' && (
            <PolicyConfigurationFormSection policyType="registry-docker" />
          )}
          <DockerRegistryFormFields
            formData={values}
            onChange={(key, value) => setFieldValue(key, value)}
            mode={mode}
          />
          {/* Edit mode: footer inside Widget */}
          {mode === 'edit' && (
            <PolicyFormFooter
              mode={mode}
              policyType="registry-docker"
              policyId={policyId}
              originalEnvironmentGroupIds={originalEnvironmentGroupIds}
            />
          )}
        </WidgetBody>
      </Widget>

      {/* Create mode: footer outside Widget with StickyFooter */}
      {mode === 'create' && (
        <PolicyFormFooter
          mode={mode}
          policyType="registry-docker"
          policyId={policyId}
          onBack={onBack}
          canGoBack={canGoBack}
        />
      )}
    </Form>
  );
}
